import * as React from 'react'

import { LockOutlined, MailOutlined } from '@ant-design/icons'
import { Button, Checkbox, Form, Input, Typography } from 'antd'
import { FC, useState } from 'react'

import { LoginProps } from 'auth/types'
import FormAlert, { FormAlertProps } from './form_alert'

const UNCONFIRMED_ERR_MSG =
  "Oops! It looks like you haven't confirmed your email address yet. Please confirm and then come back to login!"

const FORM_VALIDATE_MSGS = {
  required: '${name} is required',
  types: {
    email: 'please enter a valid email',
  },
}

const LoginForm: FC<LoginProps> = ({
  onLogin,
  onLoginSuccess,
  onPasswordReset,
  onRequestPasswordReset,
  passwordResetToken,
}) => {
  const [form] = Form.useForm()
  const [formAlert, setFormAlert] = useState<FormAlertProps>(null)
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false)

  const onFieldsChange = () => {
    if (formAlert) setFormAlert(null) // reset form error on any input change
  }

  const setFormError = (msg: string): void => {
    setFormAlert({ msg, type: 'error' })
  }

  const setFormInfo = (msg: string): void => {
    setFormAlert({ msg, type: 'info' })
  }

  const onClickRequestPasswordReset = async () => {
    setFormSubmitted(true)
    try {
      const params = await form.validateFields(['email'])
      onRequestPasswordReset({ params, onFailure: setFormError, onSuccess: setFormInfo })
    } catch (e) {
      console.log(e)
    }
  }

  const onLoginSubmit = async ({ email, password, rememberMe }) => {
    try {
      await onLogin({ email, password, rememberMe })
      onLoginSuccess()
    } catch (e) {
      if (e.message == 'Invalid credentials.') setFormError('Incorrect email or password.')
      else setFormError(e.message)
    }
  }

  const onPasswordResetSubmit = async ({ password }) => {
    onPasswordReset({ onFailure: setFormError, params: { password, passwordResetToken } })
  }

  const formAlertProps = { ...formAlert }
  const unconfirmedError = formAlert && formAlert.type == 'error' && formAlert.msg == 'Unconfirmed Email.'
  if (unconfirmedError) formAlertProps.msg = UNCONFIRMED_ERR_MSG

  const validateTrigger = formSubmitted ? 'onChange' : 'onSubmit'

  return (
    <div>
      <Form
        name="basic"
        form={form}
        initialValues={{ rememberMe: true }}
        size="middle"
        onFieldsChange={onFieldsChange}
        onFinish={passwordResetToken ? onPasswordResetSubmit : onLoginSubmit}
        validateMessages={FORM_VALIDATE_MSGS}
      >
        <FormAlert {...formAlertProps} />
        {!passwordResetToken && (
          <Form.Item name="email" rules={[{ required: true, type: 'email' }]} validateTrigger={validateTrigger}>
            <Input
              size="large"
              prefix={<MailOutlined className="site-form-item-icon" />}
              placeholder="mailbox@company.com"
            />
          </Form.Item>
        )}
        <Form.Item name="password" rules={[{ required: true }]} validateTrigger={validateTrigger}>
          <Input
            size="large"
            prefix={<LockOutlined className="site-form-item-icon" />}
            type="password"
            placeholder="Password"
          />
        </Form.Item>
        <div>
          {!passwordResetToken && (
            <Form.Item name="rememberMe" valuePropName="checked">
              <Checkbox>Remember me on this device</Checkbox>
            </Form.Item>
          )}
          {unconfirmedError ? (
            <Button type="primary" href="/users/confirmation/new" block>
              Resend Confirmation Email
            </Button>
          ) : (
            <Button htmlType="submit" type="primary" block onClick={() => setFormSubmitted(true)}>
              {passwordResetToken ? 'Reset Password' : 'Login'}
            </Button>
          )}
        </div>
        {!passwordResetToken && (
          <div style={{ marginTop: 15, display: 'flex', alignItems: 'center' }}>
            <Typography.Link onClick={onClickRequestPasswordReset} type="secondary">
              Forgotten password?
            </Typography.Link>
          </div>
        )}
      </Form>
    </div>
  )
}

export default LoginForm
